import { Mail, Paperclip } from 'lucide-react';
import type { ContactMessage } from '../../app/legacy-types';
import { formatContactDateTime } from './contactCenterModel';

type MailAddress = { name?: string | null; address?: string | null };
type MailAttachment = { index: number; filename: string; contentType?: string; size?: number; isInline?: boolean };
export type ContactMailPayload = {
  subject?: string;
  fromName?: string | null;
  fromAddress?: string | null;
  to?: MailAddress[];
  cc?: MailAddress[];
  textBody?: string;
  htmlBody?: string;
  attachments?: MailAttachment[];
  hasAttachments?: boolean;
};

const FORBIDDEN_TAGS = 'script,iframe,object,embed,form,input,button,textarea,select,option,link,base,meta,svg,math,video,audio,canvas';

function safeProtocol(value: string, attribute: string): boolean {
  const normalized = value.trim().replace(/[\u0000-\u0020]+/g, '').toLowerCase();
  if (!normalized || normalized.startsWith('#')) return true;
  if (attribute === 'href') return /^(https?:|mailto:|tel:)/.test(normalized);
  return /^(data:image\/(?:png|gif|jpe?g|webp);base64,|cid:)/.test(normalized);
}

function fallbackSanitize(html: string): string {
  return html
    .replace(/<!--([\s\S]*?)-->/g, '')
    .replace(/<(script|iframe|object|embed|form|button|textarea|select|option|svg|math|video|audio|canvas)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<\/?(?:script|iframe|object|embed|form|input|button|textarea|select|option|link|base|meta|svg|math|video|audio|canvas)\b[^>]*>/gi, '')
    .replace(/\s(?:on[a-z]+|srcdoc|formaction|srcset|background|poster|ping|style|class|id)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\s(href|src)\s*=\s*(["'])(.*?)\2/gi, (_match, attribute: string, quote: string, value: string) => safeProtocol(value, attribute.toLowerCase()) ? ` ${attribute}=${quote}${value}${quote}` : '')
    ;
}

export function sanitizeEmailHtml(html: string): string {
  const source = String(html || '');
  if (typeof DOMParser === 'undefined') return fallbackSanitize(source);
  const parsed = new DOMParser().parseFromString(source, 'text/html');
  parsed.querySelectorAll(FORBIDDEN_TAGS).forEach(node => node.remove());
  parsed.querySelectorAll<HTMLElement>('*').forEach(node => {
    for (const attribute of Array.from(node.attributes)) {
      const name = attribute.name.toLowerCase();
      const value = attribute.value;
      if (name.startsWith('on') || ['srcdoc', 'formaction', 'srcset', 'background', 'poster', 'ping', 'style', 'class', 'id'].includes(name)) node.removeAttribute(attribute.name);
      else if ((name === 'href' || name === 'src') && !safeProtocol(value, name)) node.removeAttribute(attribute.name);
    }
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
    if (node.tagName === 'IMG') node.setAttribute('referrerpolicy', 'no-referrer');
  });
  return parsed.body.innerHTML;
}

export function mailPayload(message: ContactMessage): ContactMailPayload | null {
  const value = message.payload?.mail;
  return value && typeof value === 'object' ? value as ContactMailPayload : null;
}

function addressList(rows?: MailAddress[]): string {
  return (rows || []).map(item => item.name && item.address ? `${item.name} <${item.address}>` : item.address || item.name || '').filter(Boolean).join(', ') || '—';
}

export function EmailThread({ message }: { message: ContactMessage }) {
  const mail = mailPayload(message);
  if (!mail) return <p>{message.body}</p>;
  const attachments = (mail.attachments || []).filter(item => !item.isInline);
  return <article className="contact-email-message">
    <header><span><Mail /></span><div><h3>{mail.subject || '(без темы)'}</h3><p><b>{mail.fromName || mail.fromAddress || message.sender_name || 'Отправитель'}</b>{mail.fromAddress && <small>&lt;{mail.fromAddress}&gt;</small>}</p><time>{formatContactDateTime(message.created_at)}</time></div></header>
    <dl className="contact-email-recipients"><div><dt>Кому</dt><dd>{addressList(mail.to)}</dd></div>{Boolean(mail.cc?.length) && <div><dt>Копия</dt><dd>{addressList(mail.cc)}</dd></div>}</dl>
    {mail.htmlBody ? <div className="contact-email-html" dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(mail.htmlBody) }} /> : <pre className="contact-email-text">{mail.textBody || message.body || 'Письмо не содержит текстовой части'}</pre>}
    {attachments.length > 0 && <div className="contact-email-attachments"><b>Вложения</b>{attachments.map(item => <a key={`${item.index}:${item.filename}`} href={`/api/v1/contact-center/messages/${encodeURIComponent(message.id)}/attachments/${item.index}?download=1`}><Paperclip /> {item.filename}</a>)}</div>}
  </article>;
}
